/**
 * <TutorScoreCard> — « Mon score », la carte du score masteur sur l'accueil (organism).
 *
 * L'ancienne `TutorScoreCardM.js`, rhabillée en sourdine (« le tuteur reçoit l'arène en sourdine »,
 * livre V16 p. 35) : marine et teal, sans champion, sans emoji, sans confetti. Elle tient la place
 * réservée à « Mon parcours » (le chantier de Fida), qui la remplacera sans retoucher l'accueil.
 *
 * Tout ce que l'ancienne carte disait est là, en plus serré :
 *  - les périodes (« Ce mois-ci », « Le mois dernier », « Depuis le début ») en onglets (`<Tabs>`,
 *    trois vues du même score), au lieu d'un bouton qui faisait défiler les trois — seules celles
 *    que le score a ;
 *  - le score global et le rang (une fois le seuil d'activité atteint, ou en cumulé) ;
 *  - « Bonus Top 3 actif », sur le mois en cours ;
 *  - sous le seuil, la progression (« 3 / 5 ») et ce qu'elle débloque ;
 *  - la conversion des essais, pour un masteur qui en fait ;
 *  - les quatre axes, en tuiles : chacune ouvre son explication (les textes de l'ancienne carte),
 *    l'assiduité sous la moitié passe à l'encre d'erreur et y ajoute son avertissement ;
 *  - les lives et les élèves de la période.
 *
 * La période choisie et l'explication ouverte sont de l'état d'interface : elles vivent ici.
 * `initialPeriod` et `initialOpenAxis` ouvrent une story sur l'un ou l'autre sans cliquer.
 */
import React, { useState } from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { ProgressBar } from "../../atoms/ProgressBar"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { ReferralStatTile } from "../../molecules/ReferralStatTile"
import { Tabs } from "../../molecules/Tabs"
import {
    type UITutorScore,
    type UITutorScoreAxis,
    type UITutorScoreAxisKey,
    type UITutorScorePeriodKey,
} from "../../../ports/primary/ui/types/tutorCoquilleAccueil"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"
import { isPunctualityLow, percentLabel, showsRank } from "../../../ports/primary/ui/formatters/tutorScoreFormatter"

const PERIOD_LABELS: Record<UITutorScorePeriodKey, string> = {
    current: "Ce mois-ci",
    previous: "Le mois dernier",
    global: "Depuis le début",
}
const PERIOD_ORDER: UITutorScorePeriodKey[] = ["current", "previous", "global"]

/** Le nom de chaque axe et son explication — les mots de l'ancienne carte, au tutoiement. */
const AXES: Record<UITutorScoreAxisKey, { label: string; explanation: string }> = {
    satisfaction: {
        label: "Satisfaction élève",
        explanation:
            "Après chaque séance, l'élève peut laisser une note de satisfaction.\n\n• Moy. : la moyenne des notes reçues sur la période\n• Exprimés : le pourcentage d'élèves qui ont réellement donné une note",
    },
    interaction: {
        label: "Interaction élève",
        explanation:
            "Mesure le niveau d'échanges et le bon équilibre entre tes élèves pendant la séance.\n\nCe score combine tes interactions vocales (mode Dialogue, questions et réponses, participation) et ta répartition d'attention (ne pas laisser un élève trop longtemps seul, répondre aux mains levées).\n\nPlus tu fais parler chaque élève et restes attentif à chacun, plus le score monte.",
    },
    productivity: {
        label: "Productivité élève",
        explanation:
            "Basée sur les activités soumises par l'élève (exercices réalisés et envoyés depuis l'app) et le volume de texte écrit reconnu.\n\nBonnes pratiques : privilégier des exercices courts et les faire soumettre régulièrement (la productivité monte, et tu peux faire plus de retours).",
    },
    punctuality: {
        label: "Assiduité et cadre",
        explanation:
            "Reflète ta régularité et ton engagement pendant les séances : être à l'heure, garder ta caméra allumée et utiliser un équipement adapté (ordinateur ou tablette).\n\nPlus tu es ponctuel, présent et bien installé, plus le score monte.",
    },
}

const PUNCTUALITY_WARNING =
    "\n\nTon score d'assiduité est en dessous de 50\u00A0%. Tes absences et tes retards fréquents pèsent sur ton classement et sur l'expérience de tes élèves."

const TRIAL_EXPLANATION =
    "Part des élèves que tu as rencontrés en séance d'essai qui ont ensuite activé leur abonnement.\n\nPlus tu es performant·e lors de la séance d'essai, plus les familles s'abonnent ensuite."

type Explained = UITutorScoreAxisKey | "trial"

export type TutorScoreCardProps = {
    score: UITutorScore
    /** La période ouverte au montage. Défaut : ce mois-ci. */
    initialPeriod?: UITutorScorePeriodKey
    /** L'explication ouverte au montage — pour une story. */
    initialOpenAxis?: Explained
    testID?: string
}

export function TutorScoreCard(props: TutorScoreCardProps) {
    const { score, initialPeriod = "current", initialOpenAxis, testID } = props
    const [periodKey, setPeriodKey] = useState<UITutorScorePeriodKey>(initialPeriod)
    const [open, setOpen] = useState<Explained | undefined>(initialOpenAxis)

    const periods = PERIOD_ORDER.filter((key) => score.periods[key] != null)
    const period = score.periods[periodKey] ?? score.periods.current
    const cumulative = periodKey === "global"
    const monthly = !cumulative
    const rankShown = showsRank(period, score.activityThreshold, cumulative)
    const belowThreshold = monthly && period.livesCount < score.activityThreshold
    const lowPunctuality = isPunctualityLow(period)
    const trial = period.trialConversion != null && period.trialConversion.completed > 0 ? period.trialConversion : null

    const explanation =
        open == null
            ? null
            : open === "trial"
              ? { title: "Conversion des essais", message: TRIAL_EXPLANATION }
              : {
                    title: AXES[open].label,
                    message:
                        AXES[open].explanation + (open === "punctuality" && lowPunctuality ? PUNCTUALITY_WARNING : ""),
                }

    return (
        <>
            <Card p="4" testID={testID}>
                <Stack gap="4">
                    {periods.length > 1 && (
                        // Trois vues du même score : des onglets, pas des réponses (Wajih, 03/11).
                        <Tabs<UITutorScorePeriodKey>
                            size="sm"
                            items={periods.map((key) => ({ key, label: PERIOD_LABELS[key] }))}
                            value={periodKey}
                            onChange={setPeriodKey}
                            fadeColor="surface-raised"
                            accessibilityLabel="Période"
                        />
                    )}

                    <Stack gap="1">
                        <Row gap="6" align="start" style={styles.wrap}>
                            <ReferralStatTile value={percentLabel(period.performance)} label="Score global" />
                            {rankShown && <ReferralStatTile value={`#${period.rank}`} label="Classement" />}
                        </Row>
                        {[period.rangeLabel, monthly && periodKey === "current" ? period.updatedLabel : undefined]
                            .filter((part): part is string => part != null && part !== "")
                            .map((part) => (
                                <Text key={part} size="text-xs" color="text-muted">
                                    {part}
                                </Text>
                            ))}
                    </Stack>

                    {score.bonusActive === true && periodKey === "current" && (
                        <Row gap="2">
                            <Badge text="Bonus Top 3 actif" variant="success" tinted iconName="check-circle" />
                        </Row>
                    )}

                    {belowThreshold && (
                        <Stack gap="2">
                            <Row gap="2" justify="between">
                                <Text size="text-sm" weight="bold" color="text-primary">
                                    Progression
                                </Text>
                                <Text size="text-sm" weight="bold" color="text-primary">
                                    {`${period.livesCount} / ${score.activityThreshold}`}
                                </Text>
                            </Row>
                            <ProgressBar
                                value={(period.livesCount / score.activityThreshold) * 100}
                                color="role"
                                height={6}
                            />
                            <Text size="text-xs" color="text-muted">
                                {`Complète ${score.activityThreshold} lives pour débloquer le classement et ton score complet.`}
                            </Text>
                        </Stack>
                    )}

                    <View style={styles.grid}>
                        {period.axes.map((axis) => (
                            <ScoreAxisTile
                                key={axis.key}
                                axis={axis}
                                low={axis.key === "punctuality" && lowPunctuality}
                                onPress={() => setOpen(axis.key)}
                            />
                        ))}
                    </View>

                    {trial != null && (
                        <Pressable
                            onPress={() => setOpen("trial")}
                            accessibilityRole="button"
                            accessibilityLabel="Conversion des essais, explication"
                        >
                            <Row gap="2">
                                <Text size="text-sm" color="text-primary">
                                    {`Conversion des essais : ${percentLabel(trial.rate)} (${trial.converted}/${trial.completed})`}
                                </Text>
                                <Icon name="info-circle" size={16} color="text-muted" />
                            </Row>
                        </Pressable>
                    )}

                    <Text size="text-sm" color="text-muted">
                        {[
                            countLabel(period.livesCount, "live"),
                            period.studentsCount != null ? countLabel(period.studentsCount, "élève") : null,
                        ]
                            .filter((part) => part != null)
                            .join(" · ")}
                    </Text>
                </Stack>
            </Card>

            <ConfirmDialog
                visible={explanation != null}
                title={explanation?.title ?? ""}
                message={explanation?.message}
                confirmLabel="Compris"
                danger={false}
                hideCancel
                onConfirm={() => setOpen(undefined)}
                onCancel={() => setOpen(undefined)}
            />
        </>
    )
}

type ScoreAxisTileProps = {
    axis: UITutorScoreAxis
    low: boolean
    onPress: () => void
}

/** Un axe : son nom et le rond « i », son taux, ses sous-lignes. Toute la tuile ouvre l'explication. */
function ScoreAxisTile(props: ScoreAxisTileProps) {
    const { axis, low, onPress } = props
    const { label } = AXES[axis.key]

    return (
        <Pressable
            onPress={onPress}
            accessibilityRole="button"
            accessibilityLabel={`${label}, ${percentLabel(axis.value)}, explication`}
            style={styles.tile}
        >
            <Stack gap="1">
                <Row gap="1" align="center">
                    <Text size="text-xs" weight="bold" color={low ? "text-danger" : "text-muted"} style={styles.shrink}>
                        {label}
                    </Text>
                    <Icon
                        name={low ? "exclamation-circle" : "info-circle"}
                        size={16}
                        color={low ? "text-danger" : "text-muted"}
                    />
                </Row>
                <Text size="text-xl" weight="bold" color={low ? "text-danger" : "text-primary"}>
                    {percentLabel(axis.value)}
                </Text>
                {axis.details.length > 0 && (
                    <Text size="text-xs" color={low ? "text-danger" : "text-muted"}>
                        {axis.details.join(" · ")}
                    </Text>
                )}
            </Stack>
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    wrap: {
        flexWrap: "wrap",
    },
    grid: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing["3"],
    },
    /** Deux tuiles par rangée : chacune prend la moitié moins l'écart, et passe seule sous 260 px. */
    tile: {
        flexGrow: 1,
        flexBasis: "40%",
        minWidth: 130,
        padding: theme.spacing["3"],
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors["surface-subtle"],
    },
    shrink: {
        flexShrink: 1,
    },
}))
